import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Database, Search, Users, Building2, Home, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const getToken = () => localStorage.getItem('access_token');

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load database stats');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/search?query=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupTestData = async () => {
    if (!window.confirm('Delete all test data? This cannot be undone.')) return;

    try {
      const response = await axios.delete(`${API}/admin/cleanup/test-data`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Deleted: ${response.data.deleted.properties} properties, ${response.data.deleted.rooms} rooms`);
      fetchStats();
    } catch (error) {
      toast.error('Failed to cleanup test data');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Support Console</h1>
        <p className="text-slate-600 mt-1">Platform admin tools for customer support</p>
      </div>

      {/* Database Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users size={16} />
              Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.users || 0}</div>
            <p className="text-xs text-slate-500 mt-1">
              {stats?.verified_users || 0} verified
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 size={16} />
              Organizations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.organizations || 0}</div>
            <p className="text-xs text-slate-500 mt-1">
              {stats?.properties || 0} properties
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Home size={16} />
              Rooms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.active_rooms || 0}</div>
            <p className="text-xs text-slate-500 mt-1">
              {stats?.deleted_rooms || 0} deleted
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Reservations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.reservations || 0}</div>
            <p className="text-xs text-slate-500 mt-1">
              {stats?.guests || 0} guests
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search size={20} />
            Search Customers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Input
              placeholder="Search by email, organization, or property name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={loading}>
              <Search size={16} className="mr-2" />
              Search
            </Button>
          </div>

          {searchResults && (
            <div className="space-y-4">
              {/* Users Results */}
              {searchResults.users.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Users ({searchResults.users.length})</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Role</th>
                          <th className="text-left p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.users.map((user) => (
                          <tr key={user.id} className="border-t">
                            <td className="p-2">{user.name}</td>
                            <td className="p-2">{user.email}</td>
                            <td className="p-2">
                              <span className="text-xs bg-slate-100 px-2 py-1 rounded">{user.role}</span>
                            </td>
                            <td className="p-2">
                              {user.org_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/admin-org/${user.org_id}`)}
                                >
                                  View Org
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Organizations Results */}
              {searchResults.organizations.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Organizations ({searchResults.organizations.length})</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">Created</th>
                          <th className="text-left p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.organizations.map((org) => (
                          <tr key={org.id} className="border-t">
                            <td className="p-2">{org.name}</td>
                            <td className="p-2">
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                {org.status || 'active'}
                              </span>
                            </td>
                            <td className="p-2">{org.created_at?.split('T')[0]}</td>
                            <td className="p-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/admin-org/${org.id}`)}
                              >
                                Open Org
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Properties Results */}
              {searchResults.properties.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Properties ({searchResults.properties.length})</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">City</th>
                          <th className="text-left p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.properties.map((prop) => (
                          <tr key={prop.id} className="border-t">
                            <td className="p-2">{prop.name}</td>
                            <td className="p-2">{prop.city || prop.address || 'N/A'}</td>
                            <td className="p-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/admin-property/${prop.org_id}/${prop.id}`)}
                              >
                                Open Property
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!searchResults.users.length && !searchResults.organizations.length && !searchResults.properties.length && (
                <p className="text-center text-slate-500 py-8">No results found</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cleanup Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 size={20} />
            Cleanup Tools
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <p className="text-sm text-slate-600 mb-2">
              Remove all properties with "test" in name and users with @test.com emails
            </p>
            <Button 
              variant="outline" 
              onClick={handleCleanupTestData}
              className="text-red-600 border-red-600 hover:bg-red-50"
            >
              <Trash2 size={16} className="mr-2" />
              Clean Test Data
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHome;
